import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Clock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { fmtTz } from "../lib/time";

export const CLOCKED_OUT_EVENT = 'lbt:clocked-out';

export const announceClockOut = (entry) => {
  const session = [...(entry?.sessions || [])].reverse().find((s) => s.clock_out);
  if (session) window.dispatchEvent(new CustomEvent(CLOCKED_OUT_EVENT, { detail: session }));
};

export const ClockOutConfirm = () => {
  const { authAxios } = useAuth();
  const [session, setSession] = useState(null);
  const [wrong, setWrong] = useState(false);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const onOut = (e) => { setSession(e.detail); setWrong(false); setNote(''); };
    window.addEventListener(CLOCKED_OUT_EVENT, onOut);
    return () => window.removeEventListener(CLOCKED_OUT_EVENT, onOut);
  }, []);

  if (!session) return null;

  const answer = async (correct) => {
    setSaving(true);
    try {
      await authAxios.post('/time/confirm-session', { session_id: session.id, correct, note: correct ? null : note });
      toast.success(correct ? 'Thanks for confirming!' : 'Sent to your admin to fix');
      setSession(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save your answer');
    } finally {
      setSaving(false);
    }
  };

  const minutes = session.session_minutes || 0;

  return (
    <Dialog open onOpenChange={(o) => !o && !saving && setSession(null)}>
      <DialogContent className="sm:max-w-md" data-testid="clockout-confirm-dialog">
        <DialogHeader>
          <div className="w-14 h-14 rounded-2xl bg-emerald-100 flex items-center justify-center mb-2">
            <Clock className="w-7 h-7 text-emerald-700" />
          </div>
          <DialogTitle className="text-xl">Is your time correct?</DialogTitle>
          <DialogDescription className="text-base">
            {session.project_name || 'Your shift'} · {fmtTz(session.clock_in, 'EEE, MMM d')}
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-3 gap-2 text-center bg-stone-50 rounded-xl p-4" data-testid="clockout-confirm-times">
          <div><p className="text-xs text-stone-500">In</p><p className="font-mono font-bold">{fmtTz(session.clock_in, 'h:mm a')}</p></div>
          <div><p className="text-xs text-stone-500">Out</p><p className="font-mono font-bold">{fmtTz(session.clock_out, 'h:mm a')}</p></div>
          <div><p className="text-xs text-stone-500">Worked</p><p className="font-mono font-bold">{Math.floor(minutes / 60)}h {minutes % 60}m</p></div>
        </div>
        {wrong && (
          <Textarea
            rows={3}
            autoFocus
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="What should it be? e.g. I started at 7:00, not 7:30"
            data-testid="clockout-confirm-note"
          />
        )}
        <DialogFooter className="flex-col sm:flex-row gap-2">
          {wrong ? (
            <>
              <Button variant="outline" onClick={() => setWrong(false)} disabled={saving} data-testid="clockout-confirm-back">Back</Button>
              <Button onClick={() => answer(false)} disabled={saving || !note.trim()} style={{ backgroundColor: '#d97706' }} className="hover:opacity-90 text-white" data-testid="clockout-confirm-send">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Send to admin
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => setWrong(true)} disabled={saving} data-testid="clockout-confirm-no">No, it's wrong</Button>
              <Button onClick={() => answer(true)} disabled={saving} className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="clockout-confirm-yes">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Yes, it's correct
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
